import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';
import { Link2, Zap, Loader2, CheckCircle, Trophy, Star, Calendar, Settings, Users, Award } from 'lucide-react';

interface InvitationStudentManagerProps {
  userType: 'student';
  onTabChange?: (tab: string) => void;
  onShowSuccessModal?: (show: boolean) => void;
}

const InvitationStudentManager: React.FC<InvitationStudentManagerProps> = ({
  userType,
  onTabChange,
  onShowSuccessModal,
}) => {
  const { toast } = useToast();
  const { token, refreshUserData } = useAuth();
  const { t } = useLanguage();

  const [invitationCode, setInvitationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // modal locale utilisée SEULEMENT si le parent ne fournit pas onShowSuccessModal
  const [localSuccessOpen, setLocalSuccessOpen] = useState(false);
  const [schoolJoined, setSchoolJoined] = useState<any>(null);

  useEffect(() => {
    const showModal = localStorage.getItem('showSuccessModal');
    if (showModal === 'true') {
      if (onShowSuccessModal) onShowSuccessModal(true);
      else setLocalSuccessOpen(true);
      localStorage.removeItem('showSuccessModal');
    }
  }, [onShowSuccessModal]);

  const handleJoinSchool = async () => {
    if (!invitationCode) {
      toast({
        title: t('common.error'),
        description: t('invitations.pleaseEnterCode'),
        variant: 'destructive',
      });
      return;
    }

    console.log(invitationCode);

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/api/coach/join/school`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: invitationCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to join school');
      }

      const result = await response.json();

      if(result.status === "exists") {
        toast({
          title: t('common.error'),
          description: t('invitations.alreadyRegistered'),
          variant: 'destructive',
        });
        return false;
      }

      setSchoolJoined(result.school || { name: t('invitations.yourNewSchool') });
      setInvitationCode('');

      await refreshUserData();

      if (onTabChange) {
        onTabChange('profile');
      }

      // priorité au parent si fourni
      if (onShowSuccessModal) onShowSuccessModal(true);
      else setLocalSuccessOpen(true);
    } catch (e) {
      toast({
        title: t('common.error'),
        description: t('invitations.invalidCode'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ce composant est dédié au student ; si autre userType, on ne rend rien
  if (userType !== 'student') return null;

  return (
    <>
      <div className="min-h-[500px] bg-gradient-to-br from-orange-50 via-white to-red-50 rounded-xl p-8 relative overflow-hidden">
        <div className="relative z-10">
          {/* Header */}
          <div className="text-center mb-8">
            {/*<div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-6 shadow-lg">
              <Link2 className="h-10 w-10 text-white" />
            </div>*/}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('invitations.joinSchool')}
            </h1>
            <p className="text-lg text-gray-600 max-w-md mx-auto">{/*t('invitations.toJoinDesc')*/}{t('invitations.enterInvitationCode')}</p>
          </div>

          {/* Invitation Form */}
          <Card className="max-w-md mx-auto bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-xl text-gray-900">
                <Link2 className="h-5 w-5 text-orange-500" />
                {t('invitations.invitationCode')}
              </CardTitle>
             {/* <CardDescription className="text-gray-600">
                {t('invitations.enterInvitationCode')}
              </CardDescription>*/}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                {/*<Label htmlFor="invitation-code" className="text-gray-900 font-medium">
                  {t('invitations.invitationCode')}
                </Label>*/}
                <div className="relative">
                  <Input
                    id="invitation-code"
                    placeholder={t('invitations.placeholder')}
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    className="text-center text-lg font-mono border-gray-300 focus:ring-orange-500 focus:border-orange-500 py-3"
                  />
                  <Zap className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4" />
                </div>
              </div>

              <Button
                onClick={handleJoinSchool}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 text-lg font-semibold shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    {t('invitations.connexion')}...
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5 mr-2" />
                    {t('invitations.joinSchoolBtn')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de fallback (affichée uniquement si le parent n'en gère pas) */}
      {!onShowSuccessModal && (
        <Dialog open={localSuccessOpen} onOpenChange={setLocalSuccessOpen}>
          <DialogContent className="sm:max-w-md bg-gradient-to-br from-green-50 to-blue-50 border-0 shadow-2xl">
            <DialogHeader className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>

              <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
                <Trophy className="h-6 w-6" />
                {t('invitations.congratulations')} !
              </DialogTitle>

              <DialogDescription asChild>
                <div className="text-lg text-gray-700 space-y-3 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Star className="h-5 w-5" />
                    <span className="font-semibold">
                      {t('invitations.welcomeToSchool')} {schoolJoined?.name || t('invitations.yourNewSchool')} !
                    </span>
                    <Star className="h-5 w-5" />
                  </div>

                  <p>{t('invitations.successfullJoined')}</p>

                  <div className="bg-white/60 rounded-lg p-4 mt-4 flex justify-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>Agenda</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>Étudiants</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Award className="h-4 w-4" />
                      <span>Confidentialité</span>
                    </div>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => {
                  setLocalSuccessOpen(false);
                  onTabChange?.('profile');
                  window.location.href = '/agenda';
                }}
                className="flex-1"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Voir l'Agenda
              </Button>

              <Button
                onClick={() => {
                  setLocalSuccessOpen(false);
                  onTabChange?.('profile');
                  window.location.href = '/settings';
                }}
                className="flex-1"
              >
                <Settings className="h-5 w-5 mr-2" />
                Gérer les Paramètres
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default InvitationStudentManager;
